import { type ExistingUserFeatureEnumKeysMapping } from '@solinkcorp/event-standard';

type EventStandardUserFeature = `${ExistingUserFeatureEnumKeysMapping}`;

export type UserFeature =
  | EventStandardUserFeature
  /**
   * If true, then by default very long events on the timeline will be truncated.
   * This can be overridden in the timeline.
   */
  | 'truncateLongEvents'
  /**
   * If true, user can see a limited view of agent findings (skill matches only).
   * Full findings view requires aiAgentEnabled or aiAgentRead instead.
   */
  | 'aiAgentFindingsLimited'
  /** Create private clips / private share path (web `FeaturesUtil.hasPrivateSharesEnabled`). */
  | 'privateSharesEnabled'
  /** Create public clips / public share path (web `FeaturesUtil.hasPublicSharesEnabled`). */
  | 'publicSharesEnabled'
  /** Allow sharing clip recipients (web `FeaturesUtil.canShareClips`). */
  | 'shareClipsEnabled'
  /**
   * If true, user is restricted to live camera feeds only (no recorded playback, alarms page, etc.).
   */
  | 'liveViewOnly'
  /** Create shared camera groups visible to the org (web `FeaturesUtil.hasSharedCameraGroupsEnabled`). */
  | 'sharedCameraGroupsEnabled'
  /** View shared camera groups (web `FeaturesUtil.canAccessSharedCameraGroups`). */
  | 'sharedCameraGroupsVisibility'
  /** If true, the user can manage access control doors and events. */
  | 'canManageAccessControl'
  /** Insights / native Data Explorer preview (vd web sidebar Insights row). */
  | 'previewAccess'
  /** Native Data Explorer access (SL-13530). */
  | 'dataExplorer'
  /** Permission to create/edit/delete custom dashboards and view the widget library (SL-13530). */
  | 'dataExplorerManageDashboards'
  /**
   * Access to the standalone Data Explorer VerifEye reviewer tab (SL-15900).
   * Independent of {@link dataExplorer} base access — a reviewer may hold only this.
   * Not yet surfaced via `EventStandardUserFeature` (added in event-standard 5.40.0);
   * listed explicitly until the dep bump lands (see plan §4.1/§4.2).
   */
  | 'dataExplorerVerifeye'
  /**
   * Permission to send a dashboard's events to VerifEye and assign reviewers (SL-15900).
   * Distinct from {@link dataExplorerManageDashboards}. Not yet surfaced via
   * `EventStandardUserFeature` (added in event-standard 5.40.0); listed explicitly
   * until the dep bump lands.
   */
  | 'dataExplorerManageVerifeye'
  /** Permission to share dashboards with other users (SL-13530). */
  | 'dataExplorerDashboardSharing'
  /** AI Highlights panel in dashboard view (SL-13530). */
  | 'dataExplorerAiHighlights'
  /** Ask AI / NL query surface (SL-13530). */
  | 'dataExplorerAskAi'
  /** Opt back into the new inline Events View card (default is the legacy card). */
  | 'dataExplorerNewEventCard'
  /** Allow access to the Solink mobile apps. */
  | 'mobileAccess'
  /** Camera speaker talk-down (web `sipCalls`). Defaults off. */
  | 'sipCalls'
  /**
   * Investigate tab / investigations nav (Aspen SL-12983). Defaults ON until
   * event-standard + CallHome ship the catalog key; CallHome `false` still turns it off.
   */
  | 'investigations'
  /**
   * Internal Aspen Web redesign switch (SL-16043). Gates the entire Aspen Web
   * redesign (navigation, video restyle, dashboard, and later surfaces) while
   * work is in progress. Defaults OFF. Distinct from the customer-facing launch
   * opt-in in SL-16030. Prefer {@link canSeeAspenWeb} over raw `userHasFeature`
   * so non-Solink users never see the redesign even if CallHome sets the flag.
   */
  | 'aspenWeb';

/**
 * Only define a feature here if its default is true.
 * If a feature is missing from this map, its default is false.
 */
const FEATURE_DEFAULTS: { [feature in UserFeature]?: boolean } = {
  clips: true,
  // Events-page access defaults ON: event-standard enables `events` for every
  // user type and web `FeaturesUtil.canViewEventsPage` resolves it with
  // `defaultValue: true`. Consumed via `canViewEventsPage` below.
  events: true,
  // SL-12983: default-on until CallHome/event-standard ship the key.
  investigations: true,
  mobileAccess: true,
  publicSharesEnabled: true,
  shareClipsEnabled: true,
  sharedCameraGroupsVisibility: true,
};

/** These features are not available to guest users. */
const NON_GUEST_FEATURES = new Set<UserFeature>(['cloudCamera', 'clips']);

/**
 * Minimal interface that both web's UserSession and mobile's Session satisfy.
 * Intentionally self-contained — no imports from web-specific packages.
 */
export interface UserWithFeatures {
  type: string;
  isSolinkUser?: boolean;
  profile: {
    features?: Record<string, unknown>;
  };
}

/**
 * Returns true if the user has the specified feature, false otherwise.
 *
 * Resolution order: explicit profile.features value → FEATURE_DEFAULTS → false.
 * Guest users are blocked from NON_GUEST_FEATURES regardless of their profile.
 */
export function userHasFeature(user: UserWithFeatures | null, feature: UserFeature): boolean {
  if (!user) return false;
  const raw = user.profile.features?.[feature];
  const featureFlag = raw == null ? (FEATURE_DEFAULTS[feature] ?? false) : Boolean(raw);
  const hasFeature = featureFlag && (!NON_GUEST_FEATURES.has(feature) || user.type !== 'guest');
  return hasFeature;
}

/**
 * If user only has access to manage alarm status.
 * This flag ignores the ones that allow user to access the pages other than the Alarms page.
 * If this flag is true, the user can only access the Alarms page and nothing else.
 * But if the user also have `manageAlarmSettings` feature enabled, they can manage all the alarm settings.
 */
export const canAccessAlarmStatusOnly = (user: UserWithFeatures): boolean =>
  userHasFeature(user, 'alarmAccessOnly');

export const canManageAlarmSettings = (user: UserWithFeatures): boolean =>
  userHasFeature(user, 'manageAlarmSettings') && !canAccessAlarmStatusOnly(user);

/** Check if the user has access to alarm events throughout the app not just limited to the Alarms page */
export const canViewAlarmEvents = (user: UserWithFeatures): boolean =>
  user.type !== 'guest' &&
  ((userHasFeature(user, 'alarmEventsVisibility') && !canAccessAlarmStatusOnly(user)) ||
    canManageAlarmSettings(user));

export const canViewAlarmsPage = (user: UserWithFeatures): boolean =>
  user.type !== 'guest' &&
  !userHasFeature(user, 'liveViewOnly') &&
  (userHasFeature(user, 'alarmEventsVisibility') ||
    canManageAlarmSettings(user) ||
    canAccessAlarmStatusOnly(user) ||
    userHasFeature(user, 'armDisarmAlarm'));

export const canViewExplorePage = (_user: UserWithFeatures): boolean => {
  return false;
};

export const canViewVerifeyePage = (user: UserWithFeatures): boolean =>
  user.type !== 'guest' && userHasFeature(user, 'verifEyePage');

/**
 * Whether the user may access the standalone Data Explorer VerifEye reviewer tab
 * (SL-15900). Gated solely on `dataExplorerVerifeye` — deliberately independent of
 * {@link canViewEventsPage} and the `dataExplorer` base flag, so a pure reviewer
 * (no dashboard access, no Events-page access) can still open the tab. Blocked for guests.
 */
export const canViewDataExplorerVerifEye = (user: UserWithFeatures): boolean =>
  user.type !== 'guest' && userHasFeature(user, 'dataExplorerVerifeye');

/**
 * Whether the user may send a dashboard's events to VerifEye and assign reviewers
 * (SL-15900). Gated on `dataExplorerManageVerifeye`, a distinct capability from
 * `dataExplorerManageDashboards`. Blocked for guests.
 */
export const canManageDataExplorerVerifEye = (user: UserWithFeatures): boolean =>
  user.type !== 'guest' && userHasFeature(user, 'dataExplorerManageVerifeye');

/**
 * Whether the user may access the Events page (and, in Data Explorer, the
 * optional "View on Events Page" hop).
 *
 * Mirrors web `FeaturesUtil.canViewEventsPage`: the `events` feature defaults ON
 * (see {@link FEATURE_DEFAULTS} — event-standard enables it for every user type),
 * so a user with no explicit `events` key still has access. Blocked for guests
 * and for alarm-status-only / live-view-only users.
 *
 * Note: web `FeaturesUtil` additionally blocks kiosk sessions without full access
 * (`hasNoFullAccessInKiosk`). That is a web-shell runtime concern that isn't
 * derivable from the user object, so — like the sibling {@link canViewAlarmsPage}
 * — it is not modelled here; kiosk gating stays at the route/shell layer.
 */
export const canViewEventsPage = (user: UserWithFeatures): boolean =>
  user.type !== 'guest' &&
  userHasFeature(user, 'events') &&
  !canAccessAlarmStatusOnly(user) &&
  !userHasFeature(user, 'liveViewOnly');

/**
 * Whether the user may view an access-control surface (door state + lock/unlock).
 *
 * Mirrors web `FeaturesUtil.canManageAccessControl` (the only access-control gate today),
 * with an added non-guest guard. There is no separate read-only or unlock-only flag in
 * event-standard: viewing door state and lock/unlock share the same `canManageAccessControl`
 * capability. If product later splits these into a visibility tier (analogous to
 * `alarmEventsVisibility` vs `manageAlarmSettings`), introduce a new flag and update both
 * web (door overlay polling/render) and this helper together.
 */
export const canViewAccessPage = (user: UserWithFeatures): boolean =>
  user.type !== 'guest' && userHasFeature(user, 'canManageAccessControl');

/** Whether the user may use the Solink mobile apps. */
export const canAccessMobileApp = (user: UserWithFeatures): boolean =>
  userHasFeature(user, 'mobileAccess');

/**
 * Internal Aspen Web redesign switch (SL-16043). Solink-only: the flag has no
 * effect for customer users even if CallHome sets it on their account.
 * Use this (or `useCanSeeAspenWeb`) for every Aspen Web surface — nav, video,
 * dashboard, and later redesign work — so they share one gate.
 */
export function canSeeAspenWeb(user: UserWithFeatures | null): boolean {
  if (!user) return false;
  return Boolean(user.isSolinkUser) && userHasFeature(user, 'aspenWeb');
}
